import React, { ReactNode, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { PlusOutlined } from '@ant-design/icons'
import { Dropdown, Menu } from 'antd'
import moment from 'moment'

import { PlanPeriodSummary } from 'core/application/employeeDashboard/summary/benefitSummary/getSummary'
import { getUserHSAStatusByYear, updateHSAStatus } from 'core/application/employees/employeeHSAStatus'
import CompanyUserHSAStatus from 'core/domain/companies/companyUserHSAStatus'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Modal from 'common/components/feedback/Modal'
import Button from 'common/components/general/Button'
import Icon from 'common/components/general/Icon'

import { formatYear } from 'common/dateFormatters'
import { formatMoney } from 'common/moneyFormatter'

import useCommandRequest from '../../../../../../common/hooks/useCommandRequest'
import useQueryRequest from '../../../../../../common/hooks/useQueryRequest'
import { ROUTE_PATHS } from '../../../../../../common/routePaths'
import ContributeToHsaModal from '../contributeToHsaModal'
import DaysLeftForRequestingReimbursement from '../daysLeftForRequestingReimbursement'
import SummaryItem from './SummaryItem'

import './AvailableAllowanceSummary.less'

interface AvailableAllowanceSummaryProps {
  planPeriodSummary: PlanPeriodSummary
  translate: (key: string) => string
}

const AvailableAllowanceSummary: React.FC<AvailableAllowanceSummaryProps> = ({ planPeriodSummary, translate }) => {
  const history = useHistory()
  const match = useRouteMatch()

  const [contributeToHsaModalVisible, setContributeToHsaModalVisible] = React.useState(false)
  const [reimbursedTaxYearModal, setReimbursedTaxYearModal] = React.useState(false)
  const [userContributesToHsa, setUserContributesToHsa] = useState<boolean>()
  const [selectedYear, setSelectedYear] = useState<string>('')
  const [, isGetUserHsaStatusByYearLoading, sendGetUserHsaStatusByYearQuery] =
    useQueryRequest<CompanyUserHSAStatus>(null)
  const [sendUpdateHsaCommand, isUpdateHsaLoading] = useCommandRequest()

  const handleYearValue = (e: { key: React.SetStateAction<any> }) => {
    setSelectedYear(e.key)
  }

  const handleReimbursedTaxYearModalNext = () => {
    if (selectedYear.includes('Both')) {
      redirectToReimbursedDates(planPeriodSummary.startDate, planPeriodSummary.endDate)
    } else {
      redirectToReimbursedDates(selectedYear)
    }
  }

  const handleReimbursedTaxYearModalCancel = () => {
    setReimbursedTaxYearModal(false)
    setSelectedYear('')
    setUserContributesToHsa(undefined)
  }

  const handleGetReimbursedClicked = async () => {
    const year = formatYear(planPeriodSummary.startDate)
    const result = await sendGetUserHsaStatusByYearQuery(() => getUserHSAStatusByYear(year))
    if (result) {
      history.push(
        `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${planPeriodSummary.planPeriodId}?startDate=${new Date(
          planPeriodSummary.startDate,
        ).getTime()}&endDate=${new Date(planPeriodSummary.endDate).getTime()}`,
      )
    } else {
      setContributeToHsaModalVisible(true)
      setReimbursedTaxYearModal(false)
    }
  }

  const handleGetReimbursedModalNextClicked = async () => {
    if (formatYear(planPeriodSummary.startDate) === formatYear(planPeriodSummary.endDate)) {
      await redirectToReimbursedDates(planPeriodSummary.startDate, planPeriodSummary.endDate)
    } else {
      setContributeToHsaModalVisible(false)
      setReimbursedTaxYearModal(true)
    }
  }

  const redirectToReimbursedDates = async (startDate: Date | string, endDate?: Date | string) => {
    const startYear = parseInt(formatYear(startDate), 10)

    let result = await sendUpdateHsaCommand(() =>
      updateHSAStatus({
        contributesToHSA: userContributesToHsa,
        year: startYear,
      }),
    )

    const endYear = endDate ? parseInt(formatYear(endDate), 10) : null
    if (endYear && endYear != startYear) {
      result = await sendUpdateHsaCommand(() =>
        updateHSAStatus({
          contributesToHSA: userContributesToHsa,
          year: endYear,
        }),
      )
    }

    if (result.completed) {
      history.push(
        `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${planPeriodSummary.planPeriodId}?startDate=${new Date(
          planPeriodSummary.startDate,
        ).getTime()}&endDate=${new Date(planPeriodSummary.endDate ?? '').getTime()}`,
      )
    }
  }

  const getDaysLeftForRequestingReimbursementContent = (): ReactNode => {
    if (planPeriodSummary.daysLeftForRequestingReimbursement === null) return null

    return (
      <>
        <div className="days_left_vertical_line"></div>
        <DaysLeftForRequestingReimbursement
          daysLeft={planPeriodSummary.daysLeftForRequestingReimbursement}
          startDate={planPeriodSummary.startDate}
          endDate={planPeriodSummary.endDate}
          translate={translate}
        />
      </>
    )
  }

  const getReimbursedButton = (): ReactNode => {
    const button = (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => handleGetReimbursedClicked()}
        disabled={!planPeriodSummary.allowEmployeeToSubmitExpense || isGetUserHsaStatusByYearLoading}
        loading={isGetUserHsaStatusByYearLoading}
      >
        {translate('summary.get-reimbursed-label')}
      </Button>
    )

    if (planPeriodSummary.allowEmployeeToSubmitExpense) {
      return button
    } else {
      return (
        <Tooltip position="right" title={translate('summary.get-reimbursed-tooltip')}>
          {button}
        </Tooltip>
      )
    }
  }

  const getReimbursedTaxYearModal = (): ReactNode => {
    const years = [
      formatYear(planPeriodSummary.startDate),
      formatYear(planPeriodSummary.endDate),
      formatYear(moment()),
      `Both ${formatYear(planPeriodSummary.startDate)} & ${formatYear(planPeriodSummary.endDate)}`,
    ]
    const menu = (
      <Menu onClick={handleYearValue}>
        {Array.from(new Set(years))
          .sort((first, second) => (first > second ? 1 : -1))
          .map((item) => (
            <Menu.Item key={item}>
              <span>{item}</span>
            </Menu.Item>
          ))}
      </Menu>
    )
    return (
      <Modal
        visible={reimbursedTaxYearModal}
        title="Contributing to an HSA?"
        closable
        footer={
          <>
            <Button type="default" onClick={handleReimbursedTaxYearModalCancel}>
              Back
            </Button>
            <Button type="primary" disabled={selectedYear === ''} onClick={handleReimbursedTaxYearModalNext}>
              Next
            </Button>
          </>
        }
      >
        <span className="mb-8">
          {userContributesToHsa ? (
            <span>During which tax year(s) will you contribute to an HSA?</span>
          ) : (
            <span>During which tax year(s) will you not contribute to an HSA?</span>
          )}
        </span>
        {formatYear(planPeriodSummary.startDate) === formatYear(planPeriodSummary.endDate) &&
        formatYear(planPeriodSummary.endDate) === formatYear(moment()) ? (
          <Button
            className="ant-dropdown-link grey-link ml-8 personal-dropdown"
            onClick={() => setSelectedYear(formatYear(planPeriodSummary.endDate))}
          >
            {formatYear(planPeriodSummary.endDate)}
          </Button>
        ) : (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomLeft">
            <Button className="ant-dropdown-link grey-link ml-8 personal-dropdown" onClick={(e) => e.preventDefault()}>
              {selectedYear !== '' ? (
                selectedYear
              ) : (
                <span>Select One{<Icon type="far fa-angle-down" className="ml-4" fontAwesome />}</span>
              )}
            </Button>
          </Dropdown>
        )}
      </Modal>
    )
  }

  return (
    <div className="current_balance">
      <div className="flex flex__space_between">
        <div>
          <h6 className="current_balance_label">{translate('summary.current-balance-label')}</h6>
          <h3 className="current_balance_value mb-24">
            {formatMoney(planPeriodSummary.availableBalance < 0 ? 0 : planPeriodSummary.availableBalance)}
          </h3>
          {getReimbursedButton()}
          {contributeToHsaModalVisible && (
            <ContributeToHsaModal
              visible={contributeToHsaModalVisible}
              planPeriodSummary={planPeriodSummary}
              loading={isUpdateHsaLoading}
              onContributeToHsaChanged={(value) => {
                setUserContributesToHsa(value)
              }}
              onSubmitForm={handleGetReimbursedModalNextClicked}
              onCancel={() => setContributeToHsaModalVisible(false)}
            />
          )}
          {reimbursedTaxYearModal && getReimbursedTaxYearModal()}
        </div>
        {getDaysLeftForRequestingReimbursementContent()}
      </div>
      <div className="horizontal_line mt-24 mb-16" />
      <div className="current_balance_summary">
        <SummaryItem label={translate('summary.allowance-ytd-label')} value={planPeriodSummary.allowanceYTD} />
        <div className="current_balance_summary_sign">-</div>
        <SummaryItem label={translate('summary.reimbusements-ytd-label')} value={planPeriodSummary.reimbursementYTD} />
        <div className="current_balance_summary_sign">=</div>
        <SummaryItem
          label={translate('summary.current-balance-label')}
          value={planPeriodSummary.availableBalance < 0 ? 0 : planPeriodSummary.availableBalance}
          defaultColorsOverride="#11D175"
        />
      </div>
    </div>
  )
}

export default AvailableAllowanceSummary
